import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";

import {
  fetchBudgetUserProfile,
  fetchBudgetAttachments,
} from "../../../api/fetchBudget";
import {
  fetchOccurrenceCustomer,
  fetchOccurrenceVehicle,
  fetchOccurrenceSummary,
  IOccurrenceSummary,
} from "../../../api/fetchOccurence";
import { getUserProfile } from "../../../configuration/login/loginSlice";
import { BudgetCounterProposalSubStatusEnum } from "../../../enums/budgetCounterProposalStatus";
import {
  BudgetStatusEnum,
  BudgetStatusFilterMap,
  getBudgetStatusFilter,
} from "../../../enums/budgetStatus";
import BudgetActionButtonGroup from "./components/BudgetActionButtonGroup";
import BudgetAddServiceButtons from "./components/BudgetAddServiceButtons";
import BudgetCounterProposal from "./components/BudgetCounterProposal";
import BudgetFileUpload from "./components/BudgetFileUpload";
import BudgetInfoBar from "./components/BudgetInfoBar";
import BudgetRecipientSelect from "./components/BudgetRecipientSelect";
import BudgetSummary from "./components/BudgetSummary";
import BudgetTitle from "./components/BudgetTitle";
import BudgetValuesSection from "./components/BudgetValuesSection";
import BudgetVehicleAndClientCards from "./components/BudgetVehicleAndClientCards";
import BudgetCoverageBalance from "./components/BudgetCoverageBalance";
import DetailInfoBar from "../detail/components/DetailInfoBar";
import SearchAutocomplete from "../../../components/form/searchAutocomplete";
import { useActions } from "./hooks/useActions";
import { useJourneySelector } from "./hooks/useJourneySelector";
import { useFormUpdated } from "./hooks/useFormUpdated";
import { useBudgetRoleActions } from "./hooks/useBudgetRoleActions";
import { useBudgetServices } from "./hooks/useBudgetServices";
import { useBudgetFiles } from "./hooks/useBudgetFiles";
import { buildBudgetApiPayload } from "./utils/buildBudgetApiPayload";
import {
  createEmptyBudgetFormData,
  createEmptyService,
} from "./utils/mockData";
import {
  BudgetRecipientEnum,
  IBudgetApiPayload,
  IBudgetFormData,
  IBudgetService,
  IClientData,
  IFileItem,
  IUserProfileData,
  IVehicleData,
} from "./types";
import { useNotification } from "./hooks/useNotification";
import { fetchUser } from "../../../api/fetchUser";
import { OccurrenceStepEnum } from "../../../enums/occurrenceStep";
import { useTruckerPayPlan } from "./hooks/useTruckerPayPlan";

type BudgetMode = "create" | "edit";

interface ICreateBudgetProps {
  mode?: BudgetMode;
  occurrenceId?: string;
  budgetId: string;
  onSave: (data: IBudgetFormData) => void;
  initialData?: IBudgetFormData;
  onClose?: () => void;
  redirectBack?: string;
}

const CreateBudget = (props: ICreateBudgetProps) => {
  const mode = props.mode || "create";
  const isEditMode = mode === "edit";
  const userProfile = useSelector(getUserProfile);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [occurrenceId, setOccurrenceId] = useState<string | undefined>(
    undefined,
  );

  const getUsername = async (): Promise<string> => {
    const userId = userProfile?.sub?.split(":")[1];
    if (!userId) {
      return "";
    }
    const user = await fetchUser(userId);
    return user?.name ?? "";
  };

  const getInitialVehicleData = useMemo((): IVehicleData | undefined => {
    if (isEditMode && props.initialData?.vehicleData) {
      return props.initialData.vehicleData;
    }
    if (isEditMode) {
      return undefined;
    }
    return createEmptyBudgetFormData(occurrenceId || "", props.budgetId)
      .vehicleData;
  }, [
    isEditMode,
    props.initialData?.vehicleData,
    occurrenceId,
    props.budgetId,
  ]);

  const getInitialClientData = useMemo((): IClientData | undefined => {
    if (isEditMode && props.initialData?.clientData) {
      return props.initialData.clientData;
    }
    if (isEditMode) {
      return undefined;
    }
    return createEmptyBudgetFormData(occurrenceId || "", props.budgetId)
      .clientData;
  }, [isEditMode, props.initialData?.clientData, occurrenceId, props.budgetId]);

  const getInitialRecipient = useMemo((): string | undefined => {
    if (isEditMode) {
      if (props.initialData?.recipient !== undefined) {
        return props.initialData.recipient;
      }
      return undefined;
    }
    if (props.initialData?.recipient !== undefined) {
      return props.initialData.recipient;
    }
    return BudgetRecipientEnum.TOWER;
  }, [isEditMode, props.initialData?.recipient]);

  const getInitialServices = useMemo((): IBudgetService[] => {
    if (isEditMode && props.initialData?.services) {
      return props.initialData.services;
    }
    if (isEditMode) {
      return [createEmptyService()];
    }
    return createEmptyBudgetFormData(occurrenceId || "", props.budgetId)
      .services;
  }, [isEditMode, props.initialData?.services, occurrenceId, props.budgetId]);

  const getInitialFiles = useMemo((): IFileItem[] => {
    return [];
  }, []);

  const getInitialCounterProposalValue = useMemo((): number | undefined => {
    if (isEditMode && props.initialData?.counterProposalValue !== undefined) {
      return props.initialData.counterProposalValue;
    }
    return undefined;
  }, [isEditMode, props.initialData?.counterProposalValue]);

  const getInitialCounterProposalObs = useMemo((): string | undefined => {
    if (isEditMode && props.initialData?.counterProposalObs !== undefined) {
      return props.initialData.counterProposalObs;
    }
    return undefined;
  }, [isEditMode, props.initialData?.counterProposalObs]);

  const getInitialObservation = useMemo((): string | undefined => {
    if (isEditMode && props.initialData?.observation !== undefined) {
      return props.initialData.observation;
    }
    return undefined;
  }, [isEditMode, props.initialData?.observation]);

  const getInitialUseCoverageBalance = useMemo((): boolean => {
    if (
      isEditMode &&
      props.initialData?.subscriptionBalanceToApply !== undefined
    ) {
      return true;
    }
    return false;
  }, [isEditMode, props.initialData?.subscriptionBalanceToApply]);

  const getInitialStatus = useMemo((): BudgetStatusEnum | undefined => {
    if (isEditMode && props.initialData?.status) {
      return props.initialData.status;
    }
    return BudgetStatusEnum.PENDING;
  }, [isEditMode, props.initialData?.status]);

  const [vehicleData, setVehicleData] = useState<IVehicleData | undefined>(
    getInitialVehicleData,
  );
  const [clientData, setClientData] = useState<IClientData | undefined>(
    getInitialClientData,
  );
  const [recipient, setRecipient] = useState<string | undefined>(
    getInitialRecipient,
  );
  const servicesInitializedRef = useRef(false);
  const [services, setServices] =
    useState<IBudgetService[]>(getInitialServices);
  const [files, setFiles] = useState<IFileItem[]>(getInitialFiles);
  const [counterProposalValue, setCounterProposalValue] = useState<
    number | undefined
  >(getInitialCounterProposalValue);
  const [counterProposalObs, setCounterProposalObs] = useState<
    string | undefined
  >(getInitialCounterProposalObs);
  const [useCoverageBalance, setUseCoverageBalance] = useState<boolean>(
    getInitialUseCoverageBalance,
  );
  const [profile, setProfile] = useState<BudgetRecipientEnum | undefined>(
    undefined,
  );
  const [status, setStatus] = useState<BudgetStatusEnum | undefined>(
    getInitialStatus,
  );
  const [rejectionObservation, setRejectionObservation] = useState<string>(getInitialObservation || "");

  const counterProposal = useMemo(() => {
    return counterProposalValue !== undefined && counterProposalValue > 0;
  }, [counterProposalValue]);

  const isFormUpdated = useFormUpdated({
    isEditMode,
    initialData: props.initialData,
    services,
    recipient,
    vehicleData,
    clientData,
    counterProposalValue,
    counterProposalObs,
    files,
  });

  const {
    uploadFiles: uploadFilesAction,
    handleCreate,
    handleEdit,
    handleRejectAndSend,
  } = useActions({
    budgetId: props.budgetId,
    occurrenceId: occurrenceId || "",
    onSuccess: () => {
      const formDataToSend = buildFormDataToSend();
      props.onSave(formDataToSend);
    },
  });

  const budgetServices = useBudgetServices({
    budget: props.initialData,
    services,
    setServices,
    occurrenceId: occurrenceId || "",
    isEditMode,
  });

  const budgetFiles = useBudgetFiles({
    files,
    setFiles,
    uploadFilesAction,
  });

  useEffect(() => {
    const loadProfile = async () => {
      const profileFetched = await fetchBudgetUserProfile();
      if (profileFetched) {
        setProfile(profileFetched);
      }
    };
    loadProfile();
  }, []);

  const formatHourmeter = (
    hourmeterValue: string | number | null | undefined,
  ): string | undefined => {
    if (hourmeterValue == null) {
      return undefined;
    }
    if (typeof hourmeterValue === "string" && hourmeterValue.trim() !== "") {
      return hourmeterValue;
    }
    if (typeof hourmeterValue === "number") {
      const hours = Math.floor(hourmeterValue / 3600);
      const minutes = Math.floor((hourmeterValue % 3600) / 60);
      const seconds = Math.floor(hourmeterValue % 60);
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return undefined;
  };

  const mapVehicleData = (vehicleDataResponse: any): IVehicleData => {
    const odometerStr = vehicleDataResponse.odometer
      ? `${vehicleDataResponse.odometer.toLocaleString("pt-BR")} km`
      : undefined;
    const hourmeterValue =
      vehicleDataResponse.hourMeter ?? vehicleDataResponse.hourmeter;
    const hourmeterStr = formatHourmeter(hourmeterValue);

    return {
      model:
        (
          vehicleDataResponse.model || vehicleDataResponse.vehicleName
        )?.trim() || undefined,
      year: vehicleDataResponse.year
        ? vehicleDataResponse.year.toString()
        : undefined,
      licensePlate: vehicleDataResponse.licensePlate?.trim() || undefined,
      chassis: vehicleDataResponse.chassis?.trim() || undefined,
      odometer: odometerStr,
      hourmeter: hourmeterStr,
      inWarranty: vehicleDataResponse.inWarranty ?? undefined,
      protocolNumber: vehicleDataResponse.protocolNumber?.trim() || undefined,
      contractNumber: vehicleDataResponse.contractNumber?.trim() || undefined,
    };
  };

  const mapClientData = (customerDataResponse: any): IClientData => {
    return {
      name: customerDataResponse.name || undefined,
      phone: customerDataResponse.phone || undefined,
      email: customerDataResponse.email || undefined,
    };
  };

  const resetToInitialData = () => {
    setVehicleData(getInitialVehicleData);
    setClientData(getInitialClientData);
  };

  useEffect(() => {
    setOccurrenceId(props.occurrenceId);
  }, [props.occurrenceId]);

  useEffect(() => {
    const loadOccurrenceData = async () => {
      if (isEditMode || !occurrenceId) {
        resetToInitialData();
        return;
      }

      try {
        const [vehicleDataResponse, customerDataResponse] = await Promise.all([
          fetchOccurrenceVehicle(occurrenceId),
          fetchOccurrenceCustomer(occurrenceId),
        ]);

        setVehicleData(
          vehicleDataResponse
            ? mapVehicleData(vehicleDataResponse)
            : getInitialVehicleData,
        );
        setClientData(
          customerDataResponse
            ? mapClientData(customerDataResponse)
            : getInitialClientData,
        );
      } catch (error) {
        resetToInitialData();
      }
    };

    loadOccurrenceData();
  }, [isEditMode, occurrenceId, getInitialVehicleData, getInitialClientData]);

  useEffect(() => {
    const initialRecipient = getInitialRecipient;
    if (initialRecipient !== undefined && recipient === undefined) {
      setRecipient(initialRecipient);
    }
  }, [getInitialRecipient]);

  const normalizeServicePercentages = useCallback(
    (service: IBudgetService): IBudgetService => {
      const volkstotal = service.volkstotalPercentage || 0;
      const dealership = service.dealershipPercentage || 0;
      const warranty = service.warrantyPercentage || 0;
      const clientPercentage = Math.max(
        0,
        100 - (volkstotal + dealership + warranty),
      );
      return {
        ...service,
        clientPercentage,
      };
    },
    [],
  );

  useEffect(() => {
    if (!servicesInitializedRef.current) {
      setServices(getInitialServices);
      servicesInitializedRef.current = true;
    }
  }, [getInitialServices, normalizeServicePercentages]);

  useEffect(() => {
    setFiles(getInitialFiles);
  }, [getInitialFiles]);

  useEffect(() => {
    const loadExistingFiles = async () => {
      if (isEditMode && props.budgetId && !props.budgetId.includes("budget-")) {
        try {
          const budgetAttachments = await fetchBudgetAttachments(
            props.budgetId,
          );
          const mappedFiles: IFileItem[] = budgetAttachments.map(
            (attachment) => ({
              name: attachment.name,
              type: attachment.contentType,
              size: attachment.contentSize,
              link: attachment.link,
              updatedAt: attachment.updatedAt,
            }),
          );
          setFiles(mappedFiles);
        } catch (error) {
          console.error("[CreateBudget] Error loading attachments:", error);
        }
      }
    };
    loadExistingFiles();
  }, [isEditMode, props.budgetId]);

  const orcamentoStatus = useMemo(() => {
    if (!props.initialData?.status) {
      return undefined;
    }
    const statusValue = props.initialData.status;
    // Se já for enum, retorna direto
    if (
      Object.values(BudgetStatusEnum).includes(statusValue as BudgetStatusEnum)
    ) {
      return statusValue as BudgetStatusEnum;
    }
    // Se for string, tenta converter usando BudgetStatusFilterMap
    const statusStr = String(statusValue);
    if (
      statusStr in BudgetStatusFilterMap ||
      Object.keys(BudgetStatusFilterMap).includes(statusStr)
    ) {
      return BudgetStatusFilterMap[statusStr];
    }
    // Se não encontrar, tenta usar getBudgetStatusFilter
    return getBudgetStatusFilter(statusStr);
  }, [props.initialData?.status]);

  useEffect(() => {
    if (orcamentoStatus !== undefined) {
      setStatus(orcamentoStatus);
    }
  }, [orcamentoStatus, props.initialData?.status]);

  const handleRecipientChange = useCallback(
    (recipientId?: string) => {
      const isRejected =
        isEditMode &&
        status === BudgetStatusEnum.REJECTED &&
        profile === BudgetRecipientEnum.CONSULTANT;
      if (isRejected) {
        return;
      }
      setRecipient(recipientId);
    },
    [isEditMode, status, profile],
  );

  const handleRejectAndSendCallback = useCallback(async () => {
    await handleRejectAndSend({
      rejectionObservation,
      files,
      initialData: props.initialData,
      uploadFiles: uploadFilesAction,
      profile,
      buildApiPayload: () =>
        buildBudgetApiPayload({
          occurrenceId: occurrenceId || "",
          recipient,
          clientData,
          vehicleData,
          services,
          proposalServices: props.initialData?.proposalServices,
        }),
    });
  }, [
    handleRejectAndSend,
    rejectionObservation,
    files,
    props.initialData,
    uploadFilesAction,
    profile,
    occurrenceId,
    recipient,
    clientData,
    vehicleData,
    services,
  ]);

  const userProfileData: IUserProfileData | undefined =
    userProfile || undefined;

  const validateCounterProposalInput = useCallback((): boolean => {
    if (
      counterProposal &&
      counterProposalValue !== undefined &&
      counterProposalValue > 0
    ) {
      if (!counterProposalObs || counterProposalObs.trim() === "") {
        alert(
          "A observação é obrigatória quando o valor da contraproposta for preenchido.",
        );
        return false;
      }
    }
    return true;
  }, [counterProposal, counterProposalValue, counterProposalObs]);

  const buildFormDataToSend = useCallback((): IBudgetFormData => {
    return {
      number: props.initialData?.number || "",
      occurrenceId: occurrenceId || "",
      budgetId: props.budgetId,
      userProfile: userProfileData || props.initialData?.userProfile,
      vehicleData,
      clientData,
      recipient,
      services,
      summary: budgetServices.calculatedSummary,
      counterProposal: counterProposal,
      counterProposalValue:
        counterProposalValue !== undefined && counterProposalValue > 0
          ? counterProposalValue
          : undefined,
      counterProposalObs:
        counterProposalObs && counterProposalObs.trim() !== ""
          ? counterProposalObs
          : undefined,
      profile: profile || undefined,
      status: status || undefined,
      counterProposalSubStatus:
        isEditMode &&
          counterProposal &&
          counterProposalValue !== undefined &&
          counterProposalValue > 0
          ? BudgetCounterProposalSubStatusEnum.ACCEPTED
          : undefined,
      coverageBalance: props.initialData?.coverageBalance,
      useCoverageBalance,
    };
  }, [
    occurrenceId,
    props.budgetId,
    props.initialData?.userProfile,
    userProfileData,
    vehicleData,
    clientData,
    recipient,
    services,
    budgetServices.calculatedSummary,
    counterProposal,
    counterProposalValue,
    counterProposalObs,
    profile,
    status,
    isEditMode,
    useCoverageBalance,
    props.initialData?.coverageBalance,
  ]);

  const buildApiPayload = useCallback((): IBudgetApiPayload => {
    return buildBudgetApiPayload({
      occurrenceId: occurrenceId || "",
      recipient,
      clientData,
      vehicleData,
      services,
    });
  }, [occurrenceId, recipient, clientData, vehicleData, services]);

  const handleSend = useCallback(async (): Promise<string | undefined> => {
    if (isEditMode) {
      await handleEdit({
        status,
        profile,
        orcamentoStatus,
        initialData: props.initialData,
        buildApiPayload,
        uploadFilesIfNeeded: budgetFiles.uploadFilesIfNeeded,
        validateCounterProposalInput,
        onSuccess: () => {
          const formDataToSend = buildFormDataToSend();
          props.onSave(formDataToSend);
        },
      });
      return props.budgetId;
    } else {
      let createdBudgetId: string | undefined;
      await handleCreate({
        buildApiPayload,
        uploadFilesIfNeeded: budgetFiles.uploadFilesIfNeeded,
        validateCounterProposalInput,
        onSuccess: (budgetId) => {
          createdBudgetId = budgetId;
          const formDataToSend = buildFormDataToSend();
          props.onSave(formDataToSend);
        },
      });
      return createdBudgetId;
    }
  }, [
    isEditMode,
    handleEdit,
    handleCreate,
    status,
    profile,
    orcamentoStatus,
    props.initialData,
    props.onSave,
    buildApiPayload,
    buildFormDataToSend,
    budgetFiles.uploadFilesIfNeeded,
    validateCounterProposalInput,
  ]);

  const {
    handleReject: handleRoleReject,
    handleApprove: handleRoleApprove,
    handleUpdate: handleRoleUpdate,
  } = useBudgetRoleActions({
    budgetId: props.budgetId,
    occurrenceId: occurrenceId || "",
    initialData: props.initialData,
    rejectionObservation,
    files,
    recipient,
    clientData,
    vehicleData,
    services,
    profile,
    uploadFiles: uploadFilesAction,
    buildFormDataToSend,
    onSave: props.onSave,
  });

  const {
    recipientOptions: journeyRecipientOptions,
    isConsultant: isConsultantProfile,
    isFactory: isFactoryProfile,
  } = useJourneySelector({
    isEditMode,
    profile,
    recipient,
    status,
    orcamentoStatus,
  });

  const title = isEditMode ? "Editar Orçamento" : "Criar Orçamento";

  const canSendBudget = useMemo(() => {
    const currentStatus = orcamentoStatus || status;
    if (currentStatus !== BudgetStatusEnum.PENDING) {
      return true;
    }

    const hasServices = services.length > 0;
    if (!hasServices) {
      return false; // Sem serviços, botão desabilitado
    }

    // Verificar se há pelo menos um item nos serviços
    const hasItems = services.some(
      (service) =>
        service.items &&
        service.items.length > 0 &&
        service.items.some((item) => !item.removed),
    );
    if (!hasItems) {
      return false; // Sem itens, botão desabilitado
    }

    const hasRecipient = recipient && recipient.trim() !== "";
    // Verificar se há itens com quantidade ou valor zerado
    const hasNoZeroQuantityItems = !budgetServices.hasItemsWithZeroQuantity;

    return hasRecipient && hasNoZeroQuantityItems;
  }, [
    orcamentoStatus,
    status,
    services,
    recipient,
    budgetServices.hasItemsWithZeroQuantity,
    isEditMode,
  ]);

  const recipientOptions = journeyRecipientOptions;

  const notification = useNotification();
  const truckerPayPlan = useTruckerPayPlan(
    vehicleData?.chassis || "",
    vehicleData?.odometer ? parseInt(vehicleData.odometer.replace(/\D/g, "")) : 0,
    vehicleData?.hourmeter ? parseInt(vehicleData.hourmeter.replace(/\D/g, "")) : 0,
    services,
  );

  return (
    <div className="w-full relative">
      {props.redirectBack && props.onClose && (
        <button
          type="button"
          className={`absolute top-0 ${isMobile ? "right-2" : "right-6"} z-10 text-gray-500 hover:text-gray-700 ${isMobile ? "w-8 h-8" : "w-10 h-10"} flex items-center justify-center`}
          onClick={props.onClose}
          aria-label="Fechar"
        >
          <span
            className={`rioglyph rioglyph-remove ${isMobile ? "text-size-20" : "text-size-24"}`}
          ></span>
        </button>
      )}
      {props.onClose && !props.redirectBack && (
        <button
          type="button"
          className="text-size-10 text-teal-600 underline mb-2 text-left w-fit hover:opacity-80"
          onClick={props.onClose}
        >
          Retornar para lista
        </button>
      )}

      <BudgetTitle title={title} />
      {occurrenceId ? (
        <BudgetInfoBar
          occurrenceProtocolNumber={vehicleData?.protocolNumber}
          occurrenceContractNumber={vehicleData?.contractNumber}
          budgetNumber={props.initialData?.number}
          budgetId={props.budgetId}
          profile={profile}
          status={status}
          budgetData={isEditMode ? buildFormDataToSend() : undefined}
          onRemove={isEditMode ? undefined : () => setOccurrenceId(undefined)}
        />
      ) : (
        <div className={`mb-4 mt-5 ${isMobile ? "px-2" : "px-6"}`}>
          <div className="py-6 bg-white border-b border-t border-gray-200">
            <label
              htmlFor="occurrenceSearch"
              className="text-sm font-medium mb-1 block"
            >
              Buscar ocorrência
            </label>
            <div className={isMobile ? "w-full" : "w-[300px]"}>
              <SearchAutocomplete
                id="occurrenceSearch"
                placeholder="Digite o número da ocorrência"
                fetchOptions={(searchTerm: string) =>
                  fetchOccurrenceSummary(
                    profile,
                    searchTerm,
                    OccurrenceStepEnum.THIRD_DIAGNOSIS,
                  )
                }
                optionLabel={{
                  protocolNumber: "Ocorrência",
                  vehicleChassis: "Chassi",
                }}
                onSelectedOption={(occurrence: IOccurrenceSummary) => {
                  setOccurrenceId(occurrence.uuid);
                }}
                valueRenderer={(option: IOccurrenceSummary) =>
                  `${option.protocolNumber} - ${option.vehicleChassis}`
                }
                optionRenderer={(option: IOccurrenceSummary) => (
                  <div className="truncate">
                    <div className="font-medium">{option.protocolNumber}</div>
                    <div className="text-sm text-gray-500">
                      Chassi: {option.vehicleChassis}
                    </div>
                  </div>
                )}
                onClear={() => {
                  setOccurrenceId(undefined);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <BudgetVehicleAndClientCards
        vehicleData={vehicleData}
        clientData={clientData}
        isMobile={isMobile}
      />

      <DetailInfoBar
        userProfile={userProfileData || props.initialData?.userProfile}
        origin={props.initialData?.origin || profile}
        originUserName={props.initialData?.originUserName}
        recipient={recipient}
      />

      {props.budgetId.includes("budget-") && (
        <BudgetRecipientSelect
          recipient={recipient}
          onRecipientChange={handleRecipientChange}
          options={recipientOptions}
          disabled={
            isEditMode &&
            (orcamentoStatus === BudgetStatusEnum.REJECTED ||
              status === BudgetStatusEnum.REJECTED) &&
            isConsultantProfile
          }
        />
      )}

      {
        truckerPayPlan.showCoverageBalanceBox({
          isEditMode,
          isBudgetApproved: status === BudgetStatusEnum.APPROVED,
          profile: profile,
        }) && (
          <BudgetCoverageBalance
            truckerPay={truckerPayPlan.data}
            useCoverageBalance={useCoverageBalance}
            hasCoverageBalance={truckerPayPlan.hasCoverageBalance}
            isMobile={isMobile}
            onUseCoverageBalanceChange={setUseCoverageBalance}
          />
        )}

      <div className={`mb-6 ${isMobile ? "px-4" : "px-6"}`}>
        <div className={`font-semibold mt-6 font-family-sans-pro text-[#000000] text-size-18`}>
          Valores
        </div>
        <div className="flex flex-col gap-4 m-0 p-0">
          {services.map((service, index) => {
            const serviceIndex = index;
            const proposalService = props.initialData?.proposalServices?.find(
              (proposalService) =>
                proposalService.serviceId === service.serviceId,
            );
            const proposalItems = proposalService?.items;

            return (
              <BudgetValuesSection
                key={service.id || `service-${serviceIndex}`}
                serviceId={service.serviceId}
                failureModeId={service.failureModeId}
                items={service.items}
                proposalItems={proposalItems}
                observation={service.observation}
                clientPercentage={service.clientPercentage}
                volkstotalPercentage={service.volkstotalPercentage}
                dealershipPercentage={service.dealershipPercentage}
                warrantyPercentage={service.warrantyPercentage}
                isExternalService={service.isExternalService}
                isEditMode={isEditMode}
                showPercentageFields={!props.budgetId.includes("budget-")}
                vehicleData={props.initialData?.vehicleData || vehicleData}
                serviceType={service.serviceType}
                truckerPayPlan={truckerPayPlan.data}
                onServiceChange={(serviceId, serviceData) =>
                  budgetServices.handleServiceChange(
                    serviceIndex,
                    serviceId,
                    serviceData,
                  )
                }
                onFailureModeChange={(failureModeId) =>
                  budgetServices.handleFailureModeChange(
                    serviceIndex,
                    failureModeId,
                  )
                }
                onItemChange={(itemIndex, field, value) =>
                  budgetServices.handleItemChange(
                    serviceIndex,
                    itemIndex,
                    field,
                    value,
                  )
                }
                onRemoveItem={(itemIndex) =>
                  budgetServices.handleRemoveItem(serviceIndex, itemIndex)
                }
                onAddItem={() => budgetServices.handleAddItem(serviceIndex)}
                onAddObservation={() =>
                  budgetServices.handleAddObservation(serviceIndex)
                }
                onObservationChange={(observation) =>
                  budgetServices.handleObservationChange(
                    serviceIndex,
                    observation,
                  )
                }
                onRemoveObservation={() =>
                  budgetServices.handleRemoveObservation(serviceIndex)
                }
                onRemoveService={() =>
                  budgetServices.handleRemoveService(serviceIndex)
                }
                onVolkstotalPercentageChange={(value) =>
                  budgetServices.handleVolkstotalPercentageChange(
                    serviceIndex,
                    value,
                  )
                }
                onDealershipPercentageChange={(value) =>
                  budgetServices.handleDealershipPercentageChange(
                    serviceIndex,
                    value,
                  )
                }
                onWarrantyPercentageChange={(value) =>
                  budgetServices.handleWarrantyPercentageChange(
                    serviceIndex,
                    value,
                  )
                }
                onChangeServiceType={(value) =>
                  budgetServices.handleChangeServiceType(serviceIndex, value)
                }
              />
            );
          })}
        </div>
      </div>

      <BudgetAddServiceButtons
        onAddNewService={budgetServices.handleAddNewService}
        onAddExternalService={budgetServices.handleAddExternalService}
      />

      <BudgetFileUpload
        files={files}
        budgetId={props.budgetId}
        onFileClick={budgetFiles.handleFileClick}
        onFileRemove={budgetFiles.handleFileRemove}
        onFilesAdd={budgetFiles.handleFilesAdd}
      />

      {isEditMode && (
        <BudgetCounterProposal
          counterProposal={counterProposal}
          counterProposalValue={counterProposalValue}
          counterProposalObs={counterProposalObs}
          onCounterProposalValueChange={setCounterProposalValue}
          onCounterProposalObsChange={setCounterProposalObs}
          disabled={isConsultantProfile || isFactoryProfile || !isEditMode}
        />
      )}

      <div
        className={`flex flex-col justify-end gap-4 ${isMobile ? "px-2" : "px-6"}`}
      >
        <BudgetSummary
          subtotal={budgetServices.calculatedSummary.subtotal}
          discounts={budgetServices.calculatedSummary.discounts}
          subscriptionBalanceApplied={budgetServices.calculatedSummary.subscriptionBalanceApplied}
          totalToPay={budgetServices.calculatedSummary.totalToPay}
          counterProposalValue={counterProposalValue}
          counterProposalAcceptedAt={
            props.initialData?.counterProposalAcceptedAt
          }
        />
      </div>

      <div
        className={`flex ${isMobile ? "flex-col" : "flex-row"} justify-between gap-4 ${isMobile ? "px-2" : "px-6"} ${isMobile ? "mt-6" : "mt-10"}`}
      >
        {props.budgetId.includes("budget-") && (
          <BudgetActionButtonGroup
            buttons={[
              {
                label: "Enviar orçamento",
                onClick: async () => {
                  const createdBudgetId = await handleSend();
                  const budgetIdToUse = createdBudgetId || props.budgetId;

                  // Enviar notificação de envio
                  const username = await getUsername();
                  const userId = userProfile?.sub?.split(":")[1] ?? "";
                  const vehicleChassis =
                    props.initialData?.vehicleData?.chassis ??
                    vehicleData?.chassis ??
                    "";
                  const vehicleModel =
                    props.initialData?.vehicleData?.model ??
                    vehicleData?.model ??
                    "";
                  const vehicleProtocolNumber =
                    props.initialData?.vehicleData?.protocolNumber ??
                    vehicleData?.protocolNumber ??
                    "";
                  notification.sendBudgetNotification({
                    createdBy: props.initialData?.budgetCreatedBy ?? userId,
                    message: `O(a) consultor(a) da concessionária ${username} gerou um novo orçamento${vehicleChassis ? ` para o veículo com chassi ${vehicleChassis}` : ""}${vehicleModel ? ` e modelo ${vehicleModel}` : ""}.${vehicleProtocolNumber ? ` Protocolo ${vehicleProtocolNumber}` : ""}.`,
                    budgetId: budgetIdToUse,
                  });
                },
                bsStyle: "btn-primary",
                disabled: !canSendBudget,
              },
            ]}
          />
        )}

        {/* Botões para TOWER (Fábrica) e CONSULTANT (Consultor) */}
        {isEditMode &&
          (profile === BudgetRecipientEnum.TOWER ||
            profile === BudgetRecipientEnum.CONSULTANT) &&
          (
            <div className="flex-1 flex flex-row justify-between gap-4">
              <div className="flex flex-col flex-1">
                <div className="font-bold mb-2 text-size-12 text-gray-500 pt-1">Observação</div>
                <input
                  type="text"
                  className="form-control w-full font-size-17"
                  placeholder="Insira um texto"
                  value={rejectionObservation}
                  onChange={(e) => setRejectionObservation(e.target.value)}
                />
              </div>
              <div className="flex flex-col ml-8 justify-end">
                {isFormUpdated ? (
                  <BudgetActionButtonGroup
                    buttons={[
                      {
                        label: "Enviar alterações",
                        onClick: async () => {
                          await handleRoleUpdate();

                          // Enviar notificação de alterações
                          const role =
                            profile === BudgetRecipientEnum.TOWER
                              ? "consultor(a) da fábrica"
                              : "consultor(a) da concessionária";
                          const username = await getUsername();
                          notification.sendBudgetNotification({
                            createdBy: props.initialData?.budgetCreatedBy ?? "",
                            message: `O(a) ${role} ${username} enviou alterações para o orçamento ${props.initialData?.number ?? ""}. ${props.initialData?.vehicleData?.chassis ? `Chassi: ${props.initialData?.vehicleData?.chassis}` : ""} ${props.initialData?.vehicleData?.model ? `Veículo: ${props.initialData?.vehicleData?.model}` : ""} ${props.initialData?.vehicleData?.protocolNumber ? `Chamado: ${props.initialData?.vehicleData?.protocolNumber}` : ""}`,
                            budgetId: props.budgetId,
                          });
                        },
                        bsStyle: "btn-primary",
                        disabled: !canSendBudget,
                      },
                    ]}
                  />
                ) : (
                  <BudgetActionButtonGroup
                    buttons={[
                      {
                        label: 'Recusar',
                        onClick: async () => {
                          await handleRoleReject();

                          // Enviar notificação de recusa
                          const role =
                            profile === BudgetRecipientEnum.TOWER
                              ? "consultor(a) da fábrica"
                              : "consultor(a) da concessionária";
                          const username = await getUsername();
                          notification.sendBudgetNotification({
                            createdBy: props.initialData?.budgetCreatedBy ?? "",
                            message: `O(a) ${role} ${username} recusou o orçamento ${props.initialData?.number ?? ""}. ${props.initialData?.vehicleData?.chassis ? `Chassi: ${props.initialData?.vehicleData?.chassis}` : ""} ${props.initialData?.vehicleData?.model ? `Veículo: ${props.initialData?.vehicleData?.model}` : ""} ${props.initialData?.vehicleData?.protocolNumber ? `Chamado: ${props.initialData?.vehicleData?.protocolNumber}` : ""}`,
                            budgetId: props.budgetId,
                          });
                        },
                        bsStyle: 'btn-danger',
                      },
                      {
                        label: 'Aceitar',
                        onClick: async () => {
                          await handleRoleApprove();

                          // Enviar notificação de aprovação
                          const role =
                            profile === BudgetRecipientEnum.TOWER
                              ? "consultor(a) da fábrica"
                              : "consultor(a) da concessionária";
                          const username = await getUsername();
                          notification.sendBudgetNotification({
                            createdBy: props.initialData?.budgetCreatedBy ?? "",
                            message: `O(a) ${role} ${username} aprovou o orçamento ${props.initialData?.number ?? ""}. ${props.initialData?.vehicleData?.chassis ? `Chassi: ${props.initialData?.vehicleData?.chassis}` : ""} ${props.initialData?.vehicleData?.model ? `Veículo: ${props.initialData?.vehicleData?.model}` : ""} ${props.initialData?.vehicleData?.protocolNumber ? `Chamado: ${props.initialData?.vehicleData?.protocolNumber}` : ""}`,
                            budgetId: props.budgetId,
                          });
                        },
                        bsStyle: 'btn-primary',
                      }
                    ]}
                  />
                )}
              </div>
            </div>
          )
        }

        {/* Botões para MANAGER (Gestor) */}
        {isEditMode && profile === BudgetRecipientEnum.MANAGER && (
          <div
            className={`flex-1 flex ${isMobile ? "flex-col" : "flex-row"} justify-between gap-4`}
          >
            <div className="flex flex-col flex-1">
              <div className="font-bold mb-2 text-size-12 text-gray-500 pt-1">
                Observação
              </div>
              <input
                type="text"
                className="form-control w-full font-size-17"
                placeholder="Insira um texto"
                value={rejectionObservation}
                onChange={(e) => setRejectionObservation(e.target.value)}
              />
            </div>
            <div
              className={`flex flex-col ${isMobile ? "" : "ml-8"} justify-end`}
            >
              {isFormUpdated ? (
                <BudgetActionButtonGroup
                  buttons={[
                    {
                      label: "Enviar contraproposta",
                      onClick: async () => {
                        await handleRejectAndSendCallback();

                        // Enviar notificação de contraproposta
                        const username = await getUsername();
                        notification.sendBudgetNotification({
                          createdBy: props.initialData?.budgetCreatedBy ?? "",
                          message: `O(a) gestor(a) de Frota ${username} enviou uma contraproposta para o orçamento ${props.initialData?.number ?? ""}. ${props.initialData?.vehicleData?.chassis ? `Chassi: ${props.initialData?.vehicleData?.chassis}` : ""} ${props.initialData?.vehicleData?.model ? `Veículo: ${props.initialData?.vehicleData?.model}` : ""} ${props.initialData?.vehicleData?.protocolNumber ? `Chamado: ${props.initialData?.vehicleData?.protocolNumber}` : ""}`,
                          budgetId: props.budgetId,
                        });
                      },
                      bsStyle: "btn-primary",
                    },
                  ]}
                />
              ) : (
                <BudgetActionButtonGroup
                  buttons={[
                    {
                      label: "Recusar",
                      onClick: async () => {
                        await handleRejectAndSendCallback();

                        // Enviar notificação de recusa
                        const username = await getUsername();
                        notification.sendBudgetNotification({
                          createdBy: props.initialData?.budgetCreatedBy ?? "",
                          message: `O(a) gestor(a) de Frota ${username} recusou o orçamento ${props.initialData?.number ?? ""}. ${props.initialData?.vehicleData?.chassis ? `Chassi: ${props.initialData?.vehicleData?.chassis}` : ""} ${props.initialData?.vehicleData?.model ? `Veículo: ${props.initialData?.vehicleData?.model}` : ""} ${props.initialData?.vehicleData?.protocolNumber ? `Chamado: ${props.initialData?.vehicleData?.protocolNumber}` : ""}`,
                          budgetId: props.budgetId,
                        });
                      },
                      bsStyle: "btn-danger",
                    },
                    {
                      label: "Aceitar",
                      onClick: async () => {
                        const budgetIdToUse = await handleSend();

                        // Enviar notificação de aceitação
                        const username = await getUsername();
                        notification.sendBudgetNotification({
                          createdBy: props.initialData?.budgetCreatedBy ?? "",
                          message: `O(a) gestor(a) de Frota ${username} aprovou o orçamento ${props.initialData?.number ?? ""}. ${props.initialData?.vehicleData?.chassis ? `Chassi: ${props.initialData?.vehicleData?.chassis}` : ""} ${props.initialData?.vehicleData?.model ? `Veículo: ${props.initialData?.vehicleData?.model}` : ""} ${props.initialData?.vehicleData?.protocolNumber ? `Chamado: ${props.initialData?.vehicleData?.protocolNumber}` : ""}`,
                          budgetId: budgetIdToUse || props.budgetId,
                        });
                      },
                      bsStyle: "btn-primary",
                      disabled: !canSendBudget,
                    },
                  ]}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateBudget;
